/* Copyright (c) Trimble Inc. All rights reserved. */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react';
import type { ReactNode } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

export type AlertVariant = 'info' | 'success' | 'error' | 'warning';

export interface AlertPayload {
  id: string;
  message: string;
  title?: string;
  variant?: AlertVariant;
  duration?: number;
  dismissable?: boolean;
}

export interface AlertProviderProps {
  children: ReactNode;
}

// ─── Internal store ──────────────────────────────────────────────────────────

class AlertStore {
  private listeners = new Set<() => void>();
  private static readonly DEFAULT_DURATION = 5000;
  private alerts: AlertPayload[] = [];
  private timers = new Map<string, ReturnType<typeof setTimeout>>();

  show(
    message: string,
    variant: AlertVariant = 'info',
    options: { duration?: number; id: string; title?: string; dismissable?: boolean },
  ): string {
    const id = options.id;
    const duration = options?.duration ?? AlertStore.DEFAULT_DURATION;

    // If the same alert id is reused, reset any previous auto-dismiss timer first.
    this.clearTimer(id);

    const nextAlert: AlertPayload = {
      id,
      title: options?.title,
      message,
      variant,
      duration,
      dismissable: options?.dismissable,
    };

    // Use alert id as a stable slot key so components can control visibility by hardcoded id.
    this.alerts = [...this.alerts.filter((alertItem) => alertItem.id !== id), nextAlert];
    this.emit();

    if (duration > 0) {
      this.timers.set(
        id,
        setTimeout(() => this.dismiss(id), duration),
      );
    }

    return id;
  }

  dismiss(id: string): void {
    if (!this.alerts.some((alertItem) => alertItem.id === id)) return;

    this.clearTimer(id);

    this.alerts = this.alerts.filter((alertItem) => alertItem.id !== id);
    this.emit();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): ReadonlyArray<AlertPayload> {
    return this.alerts;
  }

  private emit(): void {
    this.listeners.forEach((l) => l());
  }

  private clearTimer(id: string): void {
    const timer = this.timers.get(id);
    if (timer === undefined) return;

    clearTimeout(timer);
    this.timers.delete(id);
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

const alertStore = new AlertStore();

export const AlertContext = createContext<AlertStore>(alertStore);

// ─── AlertProvider ───────────────────────────────────────────────────────────

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => (
  <AlertContext.Provider value={alertStore}>{children}</AlertContext.Provider>
);

// ─── useAlert — trigger alerts from anywhere ─────────────────────────────────

export interface UseAlertReturn {
  showAlertMessage: (payload: AlertPayload) => string;
  dismissAlert: (id?: string) => void;
  config: AlertPayload | null;
}

export const useAlert = (targetAlertId?: string): UseAlertReturn => {
  const store = useContext(AlertContext);

  const alerts = useSyncExternalStore(
    useCallback((cb) => store.subscribe(cb), [store]),
    useCallback(() => store.getSnapshot(), [store]),
    () => [] as ReadonlyArray<AlertPayload>,
  );

  const showAlertMessage = useCallback(
    ({ id, title, message, variant = 'info', duration, dismissable }: AlertPayload) =>
      store.show(message, variant, { duration, id, title, dismissable }),
    [store],
  );

  const config = useMemo(
    () =>
      targetAlertId
        ? alerts.find((alertItem) => alertItem.id === targetAlertId) ?? null
        : alerts[0] ?? null,
    [alerts, targetAlertId],
  );

  const dismissAlert = useCallback(
    (id?: string) => {
      const dismissId = id ?? config?.id;
      if (!dismissId) return;
      store.dismiss(dismissId);
    },
    [store, config],
  );

  return { showAlertMessage, dismissAlert, config };
};
